import { useFormContext } from '@/lib/form-contexts'
import { useStore } from '@tanstack/react-form'
import { useEffect, useMemo } from 'react'

/**
 * Hook for form sections that need to encapsulate their own data transformation
 * while still integrating with parent form state
 */
export const useFormSection = ({ fieldName, initialData, getDefaultData, transformData }) => {
  const form = useFormContext()
  // Get the current form value reactively
  const currentValue = useStore(form?.store, (state) => state.values[fieldName])

  // Determine what data to use for initialization
  const sectionData = useMemo(() => {
    // If form already has a value, use it
    if (currentValue !== undefined) {
      return currentValue
    }

    // If we have initial data, transform it
    if (initialData) {
      return transformData ? transformData(initialData) : initialData
    }

    // Otherwise, use default data
    return getDefaultData ? getDefaultData() : []
  }, [currentValue, initialData, transformData, getDefaultData])

  // Initialize the form field if it's empty
  useEffect(() => {
    if (form && currentValue === undefined) {
      form.setFieldValue(fieldName, sectionData)
    }
  }, [form, fieldName, currentValue, sectionData])

  return {
    data: sectionData,
    setData: (newData, options) => form?.setFieldValue(fieldName, newData, options),
    reset: () => {
      const defaultData = getDefaultData ? getDefaultData() : []
      form?.setFieldValue(fieldName, defaultData)
    },
  }
}
